import { useState } from 'react'
import { api } from '../api/client.ts'
import { Avatar } from './Avatar.tsx'
import { CommentsPanel } from './CommentsPanel.tsx'
import { SendToChatDialog } from './SendToChatDialog.tsx'
import { RemindMe } from './RemindMe.tsx'
import { dateValue, fromInputs, timeValue } from '../lib/planner.ts'

/**
 * ДЕЛО
 *
 * Отличается от события тем, что у него нет времени начала — есть срок,
 * к которому надо успеть, и человек, который должен успеть.
 *
 * Срок необязателен: «когда-нибудь разобрать антресоль» — тоже дело,
 * и заставлять придумывать ему дату значит получить выдуманную дату.
 */

type Person = { id: string; displayName: string; avatarUrl: string | null }

export type TaskDraft = {
  id?: string
  title: string
  notes?: string | null
  dueAt?: string | null
  allDay?: boolean
  priority?: number
  assignee?: Person | null
}

export function TaskSheet({
  draft,
  people,
  onClose,
  onSaved,
}: {
  draft: TaskDraft
  people: Person[]
  onClose: () => void
  onSaved: () => void
}) {
  const editing = Boolean(draft.id)
  const due = draft.dueAt ? new Date(draft.dueAt) : null

  const [title, setTitle] = useState(draft.title)
  const [notes, setNotes] = useState(draft.notes ?? '')
  const [date, setDate] = useState(due ? dateValue(due) : '')
  const [time, setTime] = useState(due && draft.allDay === false ? timeValue(due) : '')
  const [important, setImportant] = useState((draft.priority ?? 0) > 0)
  const [assigneeId, setAssigneeId] = useState(draft.assignee?.id ?? '')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  const save = async () => {
    setBusy(true)
    setError(null)

    try {
      const body = {
        title: title.trim(),
        notes: notes.trim() || null,
        dueAt: date ? fromInputs(date, time || null).toISOString() : null,
        allDay: !time,
        priority: important ? 1 : 0,
        assigneeId: assigneeId || null,
      }

      if (draft.id) {
        await api(`/api/planner/tasks/${draft.id}`, { method: 'PATCH', body })
      } else {
        await api('/api/planner/tasks', { method: 'POST', body })
      }

      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не сохранилось')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!draft.id || !window.confirm('Удалить дело?')) return
    setBusy(true)
    try {
      await api(`/api/planner/tasks/${draft.id}`, { method: 'DELETE' })
      onSaved()
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{editing ? 'Дело' : 'Новое дело'}</h2>
          {editing && (
            <button
              className="icon-btn"
              onClick={() => setSending(true)}
              title="Отправить в переписку"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M3 20.5 21 12 3 3.5 3 10l12 2-12 2z" />
              </svg>
            </button>
          )}
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <label className="field">
          <span className="field-label">Что сделать</span>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Записать к зубному"
            maxLength={200}
            autoFocus={!editing}
          />
        </label>

        <div className="amount-row">
          <label className="field">
            <span className="field-label">К какому дню</span>
            <input
              className="input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field-label">Ко времени</span>
            <input
              className="input"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              disabled={!date}
            />
          </label>
        </div>

        {people.length > 0 && (
          <label className="field">
            <span className="field-label">Кому поручено</span>
            <select
              className="input"
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
            >
              <option value="">Мне</option>
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.displayName}
                </option>
              ))}
            </select>
          </label>
        )}

        {draft.assignee && (
          <div className="assignee-line">
            <Avatar
              id={draft.assignee.id}
              name={draft.assignee.displayName}
              url={draft.assignee.avatarUrl}
              size={22}
            />
            <span>Сейчас поручено: {draft.assignee.displayName}</span>
          </div>
        )}

        <label className="check-row">
          <input
            type="checkbox"
            checked={important}
            onChange={(e) => setImportant(e.target.checked)}
          />
          <span>Важное</span>
        </label>

        <label className="field">
          <span className="field-label">Заметка</span>
          <textarea
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Не обязательно"
            maxLength={2000}
          />
        </label>

        {editing && draft.id && (
          <>
            <h3 className="sheet-sub">Напомнить мне</h3>
            <RemindMe entityType="TASK" entityId={draft.id} title={draft.title} />

            <h3 className="sheet-sub">Обсуждение</h3>
            <CommentsPanel entityType="TASK" entityId={draft.id} />
          </>
        )}

        {error && <div className="sheet-error">{error}</div>}

        <div className="sheet-actions">
          <button className="btn-primary" disabled={busy || !title.trim()} onClick={() => void save()}>
            {busy ? '…' : 'Сохранить'}
          </button>
          {editing && (
            <button className="btn-quiet" disabled={busy} onClick={() => void remove()}>
              Удалить
            </button>
          )}
        </div>

        {sending && draft.id && (
          <SendToChatDialog
            entityType="TASK"
            entityId={draft.id}
            title={draft.title}
            onClose={() => setSending(false)}
          />
        )}
      </div>
    </div>
  )
}
